import { BookIntervalCode, WeekdayCode, WeekdayCodeName } from "@/types";
import { BusStop, IBusStop, IBusStopData } from "@/domain/entities/busStop";
import {
  IEarlyDriveData,
  IBookInterval,
  IBus,
  ILineInfo,
  ILineInfoData,
  LineInfo,
  ITurn,
  IPath,
  IFile,
  ILineFare,
  ILineSet,
  IBusStopGroupByPeriod,
  IPathGroupByPeriod,
} from "@/domain/entities/lineInfo";
import { ParseError } from "@/repositories/shared/errors/parseError";
import useGetLineTypeName from "@/use/useGetLineTypeName";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import {
  HttpBusStop,
  HttpLineInfo,
  ILineInfoParser,
  HttpTurn,
  HttpFare,
  HttpEarlyDriveInfo,
  HttpBookInterval,
  HttpBus,
  ITurnParser,
  HttpFile,
  HttpLineSet,
  HttpBusStopByTurnPath,
  HttpPathGroup,
} from "../lineInfoRepository.types";
import useGetLineRouteName from "@/use/useGetLineRouteName";
import { parseBookInterval } from "./bookIntervalParser";
import dayjs from "dayjs";

function parseBusStop(station: HttpBusStop, index: number): IBusStop {
  const busStopData: IBusStopData = {
    id: station.uid,
    number: station.no,
    name: station.name,
    shortName: station.shortName,
    order: index,
    departureTime: station.departureTime?.trim(),
    arrivalTime: station.arrivalTime?.trim(),
    isFirstStop: station.beginYn,
    isLastStop: station.endYn,
    isGetOnStop: station.onYn,
    isGetOffStop: station.offYn,
    isTransitStop: station.transYn,
    isReturnStop: station.returnYn,
    isDisplayStop: station.displayYn,
    isOmitted: station.omittedYn,
    isTemp: station.tempYn,
    shouldShowStreetView: station.streetViewYn,
    shouldShowBusStopLayout: station.useStationLayoutYn,
    latitude: station.lat,
    longitude: station.lng,
    direction: "",
    transferBusStops: station.trans?.routes,
    transferGuide: station.trans?.transText,
    isFavorite: station.isFavorite ?? false,
    useBeginDtm: dayjs(station.useBeginDtm).format("YYYY-MM-DD HH:mm"),
    useEndDtm: station.useEndDtm
      ? dayjs(station.useEndDtm).format("YYYY-MM-DD HH:mm")
      : undefined,
  };

  return new BusStop(busStopData);
}

function parseBusStopList(stations: HttpBusStop[]): IBusStop[] {
  // 화면 표시 안하는 정류장 필터링
  const filteredStations = stations?.filter((station) => station.displayYn);

  return filteredStations?.map((station: HttpBusStop, index: number) =>
    parseBusStop(station, index)
  );
}

function getFare(
  fares: HttpFare[],
  turnId: string,
  bookIntervalCode: string
): number | undefined {
  return (
    fares?.find((fare) => {
      if (fare.lineTurnUid) {
        return (
          fare.lineTurnUid === turnId &&
          fare.bookIntervalCode === bookIntervalCode
        );
      }
      return fare.bookIntervalCode === bookIntervalCode;
    })?.fareAmount ?? 0
  );
}

export function getLineFare(fares: HttpFare[], turnId: string): ILineFare {
  const lineFare: ILineFare = {
    dailyFare: getFare(fares, turnId, BookIntervalCode.DAY),
    monthlyFare: getFare(fares, turnId, BookIntervalCode.MONTH),
  };

  return lineFare;
}

function convertWeekdayName(weekdayCode: string): WeekdayCodeName {
  const weekdayCodeMap: Record<string, WeekdayCodeName> = {
    [WeekdayCode.MON]: WeekdayCodeName.MON,
    [WeekdayCode.TUE]: WeekdayCodeName.TUE,
    [WeekdayCode.WED]: WeekdayCodeName.WED,
    [WeekdayCode.THU]: WeekdayCodeName.THU,
    [WeekdayCode.FRI]: WeekdayCodeName.FRI,
    [WeekdayCode.SAT]: WeekdayCodeName.SAT,
    [WeekdayCode.SUN]: WeekdayCodeName.SUN,
    [WeekdayCode.HOLI]: WeekdayCodeName.HOLI,
  };

  return weekdayCodeMap[weekdayCode] ?? WeekdayCodeName.NONE;
}

function sortByWeekday(earlyDriveInfos: IEarlyDriveData[]): IEarlyDriveData[] {
  // 요일에 따라 정렬(월,화,수,목,금,토,일,휴 순)
  const sortedWeekDay = Object.values(WeekdayCode);

  const sortedEarlyDriveInfos = earlyDriveInfos?.sort((a, b) => {
    const indexOfA = sortedWeekDay.findIndex(
      (weekday) => weekday === a.weekdayCode
    );
    const indexOfB = sortedWeekDay.findIndex(
      (weekday) => weekday === b.weekdayCode
    );
    return indexOfA - indexOfB;
  });

  return sortedEarlyDriveInfos;
}

function getEarlyDriveInfos(
  earlyDriveInfos: HttpEarlyDriveInfo[] | undefined
): IEarlyDriveData[] | undefined {
  if (!earlyDriveInfos) return;

  const parsedEarlyDriveInfos = earlyDriveInfos?.map((earlyDriveInfo) => {
    return {
      weekdayCode: earlyDriveInfo.weekdayCode,
      weekdayName: convertWeekdayName(earlyDriveInfo.weekdayCode),
      earlyMinute: earlyDriveInfo.earlyMinute,
    };
  });

  const sortedEarlyDriveInfos = sortByWeekday(parsedEarlyDriveInfos);

  return sortedEarlyDriveInfos;
}

function getBookIntervalWithCode(
  data: HttpBookInterval[] | undefined,
  code: BookIntervalCode
): HttpBookInterval | undefined {
  return data?.find((bookInterval) => bookInterval.bookIntervalCode === code);
}

function parseBookIntervalWithCode(
  data: HttpBookInterval[] | undefined,
  code: BookIntervalCode
): IBookInterval | undefined {
  const httpBookInterval = getBookIntervalWithCode(data, code);
  if (!httpBookInterval) return undefined;
  return parseBookInterval(httpBookInterval);
}

function parseBusImage(file: HttpFile): IFile {
  return {
    fileUid: file.fileUid,
    fileName: file.fileName,
    fileSubTypeCode: file.fileSubTypeCode,
    fileSubTypeCodeName: file.fileSubTypeCodeName,
    fileUrl: file.fileUrl,
  };
}

export function parseBus(httpBus: HttpBus): IBus {
  return {
    allocUid: httpBus.allocUid,
    busOrder: httpBus.busOrder,
    carNo: httpBus.carNo,
    seatCount: httpBus.seatCount,
    bookCount: httpBus.bookCount,
    boardCount: httpBus.boardCount,
    remainCount: httpBus.remainCount,
    allocStatusCode: httpBus.allocStatusCode,
    busImages: httpBus.busImages?.map(parseBusImage),
  };
}

export function sortBuses(buses: IBus[]): IBus[] {
  const sortedBuses = buses.sort((a, b) => a.busOrder - b.busOrder);
  return sortedBuses;
}

function parsePath(path: (number | string)[]): IPath {
  return {
    longitude: parseFloat(path[0] as string),
    latitude: parseFloat(path[1] as string),
  };
}

function parseLineSet(lineSet: HttpLineSet): ILineSet {
  return {
    lineSetCodeUid: lineSet.lineSetCodeuid,
    name: lineSet.name,
    displayText: lineSet.shortName ? lineSet.shortName : lineSet.name,
    isToday: lineSet.todayYn === "Y",
  };
}

function parseBusStopGroupByPeriod(
  data: HttpBusStopByTurnPath
): IBusStopGroupByPeriod {
  const busStopGroup: IBusStopGroupByPeriod = {
    useBeginDtm: dayjs(data.useBeginDtm).format("YYYY-MM-DD HH:mm"),
    useEndDtm: data.useEndDtm
      ? dayjs(data.useEndDtm).format("YYYY-MM-DD HH:mm")
      : undefined,
    busStops: parseBusStopList(data.stations),
  };

  return busStopGroup;
}

function sortBusStopGroupsByPeriod(
  groups: IBusStopGroupByPeriod[]
): IBusStopGroupByPeriod[] {
  return groups.sort((a, b) => {
    const beginDiff = dayjs(a.useBeginDtm).diff(dayjs(b.useBeginDtm));
    if (beginDiff !== 0) return beginDiff;

    const defaultEnd = dayjs("9999-12-31");
    const endA = dayjs(a.useEndDtm ?? defaultEnd);
    const endB = dayjs(b.useEndDtm ?? defaultEnd);

    return endA.diff(endB);
  });
}

function parsePathGroupByPeriod(data: HttpPathGroup): IPathGroupByPeriod {
  const pathGroup: IPathGroupByPeriod = {
    lineTurnUid: data.lineTurnUid,
    useBeginDtm: dayjs(data.useBeginDtm).format("YYYY-MM-DD HH:mm"),
    useEndDtm: data.useEndDtm
      ? dayjs(data.useEndDtm).format("YYYY-MM-DD HH:mm")
      : undefined,
    path: data.path.map(parsePath) ?? [],
  };

  return pathGroup;
}

@injectable()
export class LineInfoParser implements ILineInfoParser {
  constructor() {}

  toDomain(data: HttpLineInfo): Result<ILineInfo, ParseError> {
    try {
      const turns: ITurn[] = [];
      const schedules: Array<string> = [];

      data.turns.map((httpTurn: HttpTurn) => {
        schedules.push(httpTurn?.beginStation?.departureTime?.trim());

        const busStops = parseBusStopList(httpTurn.stations);
        const busStopGroupsByPeriod =
          httpTurn.stationsArray?.map(parseBusStopGroupByPeriod) ?? [];
        const buses = httpTurn.buses?.map(parseBus);

        const turn: ITurn = {
          id: httpTurn?.uid,
          turnNum: httpTurn?.no,
          name: httpTurn?.name,
          lineTimeCode: httpTurn.lineTimeCode,
          lineTimeName: httpTurn.lineTimeName,
          busStops: busStops,
          firstStop: busStops?.[0],
          lastStop: busStops?.[busStops.length - 1],
          lineSets: httpTurn.lineSets?.map(parseLineSet) ?? [],
          lineFare: getLineFare(data.fares, httpTurn.uid),
          earlyDriveInfos: getEarlyDriveInfos(httpTurn?.earlyDriveWeekly),
          detourPaths: httpTurn.detourPaths?.map(parsePath),
          bookYn: httpTurn.bookYn,
          buses: buses ? sortBuses(buses) : undefined,
          bookIntervals: httpTurn.bookIntervals?.map(parseBookInterval),
          useBeginDtm: dayjs(httpTurn.useBeginDtm).format("YYYY-MM-DD HH:mm"),
          useEndDtm: httpTurn.useEndDtm
            ? dayjs(httpTurn.useEndDtm).format("YYYY-MM-DD HH:mm")
            : undefined,
          busStopGroupsByPeriod: sortBusStopGroupsByPeriod(
            busStopGroupsByPeriod
          ),
          pathGroupsByPeriod: httpTurn.paths?.map(parsePathGroupByPeriod) ?? [],
          paths: httpTurn.props?.path?.map(parsePath) ?? [],
        };
        turns.push(turn);
      });

      const lineInfoData: ILineInfoData = {
        id: data.uid,
        lineNum: data.no,
        routeName: data.name,
        lineNameDisplayText: useGetLineRouteName(
          data.lineTypeCode,
          data.name,
          data.no
        ),
        campusCode: data.campusCode,
        campusName: data.campusName,
        lineDirectionCode: data.lineDirectionCode,
        lineDirectionName: data.lineDirectionName,
        lineDistrictCode: data.lineDistrictCode,
        lineDistrictName: data.lineDistrictName,
        turns: turns,
        schedules: schedules,
        isBusRunning: true,
        lineTypeCode: data.lineTypeCode,
        lineTypeName: useGetLineTypeName(data.lineTypeCode),
        district: data?.districtName ?? "",
        dailyBookInterval: parseBookIntervalWithCode(
          data.bookIntervals,
          BookIntervalCode.DAY
        ),
        monthlyBookInterval: parseBookIntervalWithCode(
          data.bookIntervals,
          BookIntervalCode.MONTH
        ),
        detourBeginDtm: data.detourBeginDtm
          ? dayjs(data.detourBeginDtm).format("YYYY-MM-DD HH:mm:ss")
          : undefined,
        detourEndDtm: data.detourEndDtm
          ? dayjs(data.detourEndDtm).format("YYYY-MM-DD HH:mm:ss")
          : undefined,
        detourReason: data.detourReason,
      };
      const lineInfo = new LineInfo(lineInfoData);
      return ok(lineInfo);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}

@injectable()
export class TurnParser implements ITurnParser {
  constructor() {}

  toDomain(data: HttpTurn): Result<ITurn, ParseError> {
    try {
      const schedules: Array<string> = [];
      schedules.push(data?.beginStation?.departureTime?.trim());

      const busStops = parseBusStopList(data.stations);
      const busStopGroupsByPeriod =
        data.stationsArray?.map(parseBusStopGroupByPeriod) ?? [];

      const turn: ITurn = {
        id: data?.uid,
        turnNum: data?.no,
        name: data?.name,
        busStops: busStops,
        firstStop: busStops?.[0],
        lastStop: busStops?.[busStops.length - 1],
        lineSets: data.lineSets?.map(parseLineSet) ?? [],
        lineFare: getLineFare(data.fares ?? [], data.uid),
        earlyDriveInfos: getEarlyDriveInfos(data?.earlyDriveWeekly),
        bookYn: data.bookYn,
        useBeginDtm: dayjs(data.useBeginDtm).format("YYYY-MM-DD HH:mm"),
        useEndDtm: data.useEndDtm
          ? dayjs(data.useEndDtm).format("YYYY-MM-DD HH:mm")
          : undefined,
        busStopGroupsByPeriod: sortBusStopGroupsByPeriod(busStopGroupsByPeriod),
        pathGroupsByPeriod: data.paths?.map(parsePathGroupByPeriod) ?? [],
        paths: data.props?.path?.map(parsePath) ?? [],
      };
      return ok(turn);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}
